/**
 * Dynamically hides variant option values (and entire option groups) that
 * are not part of any real product variant for the currently selected
 * combination — instead of Dawn's default behaviour of just greying them
 * out. If an option group ends up with only one selectable value left
 * (e.g. "Design" when a color only ships in one design), the whole group
 * is hidden rather than shown with a single disabled-looking choice.
 *
 * Works with any product using Dawn's standard variant-picker markup
 * (button or swatch picker types) — nothing here is hard-coded to a
 * specific product, option name, or option count. Runs after Dawn's own
 * `variant-selects` change handler has toggled the `.disabled` class, and
 * once on load to match the server-rendered initial state.
 *
 * When collapsing a group down to its one remaining real value, that value
 * is also auto-selected. Without this, switching from an option (like a
 * color that has several designs) to one that only has a single design
 * left a stale, now-hidden selection checked — so no variant matched the
 * checked inputs and the price/media/add-to-cart never updated. Forcing
 * the sole remaining value to be checked (and re-dispatching a change
 * event so Dawn recalculates the variant) fixes that.
 */
(function () {
  function updateFieldsetVisibility(fieldset) {
    const radios = fieldset.querySelectorAll('input[type="radio"]');
    if (!radios.length) return null;

    let visibleCount = 0;
    let lastVisibleInput = null;
    radios.forEach((input) => {
      const isDisabled = input.classList.contains('disabled');
      const label = fieldset.querySelector(`label[for="${input.id}"]`);
      input.classList.toggle('variant-option-value--hidden', isDisabled);
      if (label) label.classList.toggle('variant-option-value--hidden', isDisabled);
      if (!isDisabled) {
        visibleCount += 1;
        lastVisibleInput = input;
      }
    });

    // Hide the whole option group when there's nothing left to actually choose between.
    const shouldHide = radios.length > 1 && visibleCount <= 1;
    fieldset.classList.toggle('variant-option--hidden', shouldHide);

    // If we just collapsed this group to its one remaining real value, make
    // sure that value is actually checked. A stale selection left over from
    // a previous (now hidden) choice would otherwise mean no variant matches
    // the checked inputs at all.
    if (shouldHide && visibleCount === 1 && lastVisibleInput && !lastVisibleInput.checked) {
      lastVisibleInput.checked = true;
      return lastVisibleInput;
    }

    return null;
  }

  function updateAllFieldsets(variantSelects) {
    const forcedInputs = [];
    variantSelects.querySelectorAll('fieldset.product-form__input').forEach((fieldset) => {
      const forced = updateFieldsetVisibility(fieldset);
      if (forced) forcedInputs.push(forced);
    });

    if (forcedInputs.length) {
      // Re-trigger variant recalculation now that stale hidden selections
      // have been corrected to the only real option left. Dispatching once
      // (bubbling up to variant-selects) is enough to re-run both Dawn's
      // own handler and this one; by the second pass nothing needs forcing
      // so this doesn't loop.
      forcedInputs[forcedInputs.length - 1].dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function initVariantSelects(variantSelects) {
    if (variantSelects.dataset.optionVisibilityInit) return;
    variantSelects.dataset.optionVisibilityInit = 'true';

    // Match the server-rendered initial state.
    updateAllFieldsets(variantSelects);

    // Dawn's own listener (added in the variant-selects constructor) runs
    // first and updates the `.disabled` classes; this listener is attached
    // afterwards, so on the same 'change' event it always runs after that
    // update has completed.
    variantSelects.addEventListener('change', () => updateAllFieldsets(variantSelects));
  }

  function scan() {
    document.querySelectorAll('variant-selects').forEach(initVariantSelects);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }

  // Quick-add modals and similar can inject a fresh <variant-selects> after
  // the initial page load; keep watching for those too so this works
  // anywhere a variant picker can appear, not just the main product page.
  new MutationObserver(scan).observe(document.body, { childList: true, subtree: true });
})();
